// Plan de séance : exercices, horaires, alternative, et chrono de séance en
// direct. Logique pure, horloge injectée (`now` en millisecondes).
//
// Deux leçons de Playbook BCL :
//  - l'alternative est rangée DANS l'exercice ; basculer ÉCHANGE les deux
//    contenus. Tout ce qui lit le plan lit donc l'exercice actif, sans rien
//    savoir de l'alternative ;
//  - le chrono se cale sur l'HEURE RÉELLE (T0 + durées), jamais sur un
//    compteur : pause = décalage, « +2 min » = décalage, « suivant » =
//    avance ; un téléphone mis en veille retombe juste.

import type { ExoGroup } from './groups';

export type Category = 'echauffement' | 'technique' | 'tactique' | 'physique' | 'jeu' | 'retour_calme';

export const CATEGORY_LABEL: Record<Category, string> = {
  echauffement: 'Échauffement', technique: 'Technique', tactique: 'Tactique',
  physique: 'Physique', jeu: 'Jeu', retour_calme: 'Retour au calme',
};

export interface ItemContent {
  title: string;
  minutes: number;
  category: Category;
  description?: string | null;
  exercise_id?: string | null;
  /** Un play travaillé dans la séance (D-043). */
  play_id?: string | null;
  /** Le drill de l'élément (D-098) : absent, celui de l'exercice de la bibliothèque ; null, aucun pour cette séance. */
  drill_id?: string | null;
}

export interface PlanItem extends ItemContent {
  id: string;
  alt?: ItemContent | null;
  /** Les groupes du créneau (D-052) : ils restent en place si on passe au plan B. */
  groups?: ExoGroup[] | null;
  /** Repris de ce que propose le club (D-093) : un objectif du cycle, le thème du mois. */
  objective_id?: string | null;
  theme_id?: string | null;
}

export const totalMinutes = (items: Pick<PlanItem, 'minutes'>[]) => items.reduce((n, i) => n + (i.minutes || 0), 0);

export interface Slot { item: PlanItem; start: Date; end: Date }

/** Heure de début et de fin de chaque exercice, à partir du début de séance. */
export function timeline(items: PlanItem[], start: Date): Slot[] {
  let t = start.getTime();
  return items.map(item => {
    const s = new Date(t);
    t += (item.minutes || 0) * 60_000;
    return { item, start: s, end: new Date(t) };
  });
}

/** Bascule l'alternative : elle devient l'exercice, l'exercice devient l'alternative. */
export function swapAlt(item: PlanItem): PlanItem {
  if (!item.alt) return item;
  const { id, alt, groups, ...active } = item;
  return groups === undefined ? { id, ...alt, alt: active } : { id, ...alt, alt: active, groups };
}

export function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const out = list.slice();
  const [x] = out.splice(from, 1);
  out.splice(to, 0, x!);
  return out;
}

// --- Chrono de séance --------------------------------------------------------

export interface Live {
  t0: number;                 // lancement
  index: number;              // exercice en cours
  curEndAt: number;           // fin prévue de l'exercice en cours (heure réelle)
  exoStartedAt: number;
  pausedAt: number | null;
  done: boolean;
  log: { index: number; actualMs: number; skipped: boolean }[];
}

const MIN = 60_000;

export function startLive(items: PlanItem[], now: number): Live {
  return { t0: now, index: 0, curEndAt: now + (items[0]?.minutes ?? 0) * MIN, exoStartedAt: now, pausedAt: null, done: items.length === 0, log: [] };
}

/** Temps restant sur l'exercice en cours (négatif = dépassé). */
export const remainingMs = (l: Live, now: number) => l.curEndAt - (l.pausedAt ?? now);

export function pause(l: Live, now: number): Live {
  return l.pausedAt !== null || l.done ? l : { ...l, pausedAt: now };
}

/** Reprendre : tout est décalé de la durée de la pause (elle ne compte pas). */
export function resume(l: Live, now: number): Live {
  if (l.pausedAt === null) return l;
  const shift = now - l.pausedAt;
  return { ...l, pausedAt: null, curEndAt: l.curEndAt + shift, exoStartedAt: l.exoStartedAt + shift };
}

/** Prolonger l'exercice en cours (y compris s'il est déjà dépassé). */
export function addMinutes(l: Live, minutes: number, now: number): Live {
  const base = Math.max(l.curEndAt, l.pausedAt ?? now);
  return { ...l, curEndAt: base + minutes * MIN };
}

/** Passer à l'exercice suivant (fin de l'exercice, ou « passer » s'il en reste). */
export function next(l: Live, items: PlanItem[], now: number, skipped = false): Live {
  const at = l.pausedAt ?? now;
  const log = [...l.log, { index: l.index, actualMs: at - l.exoStartedAt, skipped }];
  const i = l.index + 1;
  if (i >= items.length) return { ...l, log, done: true, pausedAt: null };
  return { ...l, log, index: i, exoStartedAt: at, curEndAt: at + (items[i]!.minutes || 0) * MIN, pausedAt: null };
}

/** Heure de fin projetée de la séance, au rythme actuel. */
export function projectedEnd(l: Live, items: PlanItem[], now: number): number {
  const cur = Math.max(l.curEndAt + (l.pausedAt !== null ? now - l.pausedAt : 0), now);
  return cur + totalMinutes(items.slice(l.index + 1)) * MIN;
}

/** Avance (négatif) ou retard (positif) sur le plan, en minutes. */
export const offsetMinutes = (l: Live, items: PlanItem[], now: number) =>
  Math.round((projectedEnd(l, items, now) - (l.t0 + totalMinutes(items) * MIN)) / MIN);
